import _debounce from 'lodash/debounce';

// TODO: Don't allow to drag non-loop carousel outside of screen (drag coefficient)
// TODO: rtl support + autodetect
// TODO: showcase - async loading
// TODO: tabindex
// TODO: recalc perPage on wiewport change

// https://medium.com/@lsnrae/if-you-must-use-a-carousel-make-it-accessible-977afd0173f4
// https://ux.stackexchange.com/questions/118607/content-slider-carousel-accessibility-best-practices

function getViewportWidth() {
  if (!window) return 0;
  return Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
}

function normalizePerPage(value) {
  const perPage = parseInt(value, 10);

  if (Number.isNaN(perPage)) return 1;

  return Math.max(perPage, 1);
}

export default {
  props: {
    value: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: [Number, Object],
      default: 1,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    rtl: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: true,
    },
    easing: {
      type: String,
      default: 'ease-out',
    },
    duration: {
      type: Number,
      default: 200,
    },
    draggable: {
      type: Boolean,
      default: true,
    },
    threshold: {
      type: Number,
      default: 20,
    },
    ignoreDragging: {
      type: Array,
      default: () => ([
        'textarea',
        'option',
        'input',
        'select',
      ]),
    },
    handleTouchStartEvent: {
      type: Function,
      default: (e) => {
        e.stopPropagation();
      },
    },
    handleTouchMoveEvent: {
      type: Function,
      default: (e) => {
        e.stopPropagation();
        e.preventDefault();
      },
    },
    handleTouchEndEvent: {
      type: Function,
      default: (e) => {
        e.stopPropagation();
      },
    },
    handleMouseDownEvent: {
      type: Function,
      default: (e) => {
        e.stopPropagation();
        e.preventDefault();
      },
    },
    handleMouseMoveEvent: {
      type: Function,
      default: (e) => {
        e.stopPropagation();
        e.preventDefault();
      },
    },
    handleMouseUpEvent: {
      type: Function,
      default: (e) => {
        e.stopPropagation();
      },
    },
  },
  data() {
    return {
      itemsCount: 0,
      offset: 0,
      viewportWidth: getViewportWidth(),
      currentIndex: 0,
      mutableValue: 0,
      dragStartX: 0,
      dragStartY: 0,
      dragEndX: 0,
      dragPreventClick: false,
      isDragging: false,
      isPointerDown: false,
      isTransitionEnabled: false,
      isHorizontalDrag: false,
      observer: null,
    };
  },
  computed: {
    actualPerPage() {
      if (typeof this.perPage === 'object') {
        let perPage = 1;
        Object.keys(this.perPage)
          .map((key) => parseInt(key, 10))
          .sort((a, b) => a - b)
          .forEach((breakpoint) => {
            if (this.viewportWidth >= breakpoint) {
              perPage = normalizePerPage(this.perPage[breakpoint]);
            }
          });
        return perPage;
      }
      return normalizePerPage(this.perPage);
    },
    clonesCount() {
      if (!this.loop || this.actualPerPage > this.itemsCount) return 0;
      return this.actualPerPage + Math.floor(this.actualPerPage / 2);
    },
    containerStyles() {
      let cursor = null;

      if (this.draggable) {
        cursor = this.isPointerDown ? '-webkit-grabbing' : '-webkit-grab';
      }

      return {
        width: '100%',
        overflow: 'hidden',
        direction: this.rtl ? 'rtl' : 'ltr',
        cursor,
      };
    },
    frameStyles() {
      const duration = this.isTransitionEnabled ? this.duration : 0;
      const transition = `transform ${duration}ms ${this.easing}`;
      return {
        position: 'relative',
        display: 'flex',
        width: '100%',
        transform: `translate3d(${this.offset}, 0, 0)`,
        transition,
      };
    },
    itemStyles() {
      const width = `${100 / this.actualPerPage}%`;
      return {
        flex: `0 0 ${width}`,
        maxWidth: width,
        minWidth: 0,
        outline: 'none',
      };
    },
  },
  watch: {
    actualPerPage(val, oldVal) {
      if (val === oldVal) return;
      this.setMutableValue(this.mutableValue);
      this.adjustFrame();
    },
    loop(val, oldVal) {
      if (val === oldVal) return;
      this.setMutableValue(this.mutableValue);
      this.adjustFrame();
    },
    value(val, oldVal) {
      if (val === oldVal) return;
      if (val === this.mutableValue) return;
      this.setMutableValue(val);
      this.adjustFrame();
    },
  },
  mounted() {
    this.calcItemsCount();
    this.setMutableValue(this.value);
    this.emitInput();
    this.adjustFrame();
    this.setEventListeners();
    this.setMutationObserver();
  },
  beforeDestroy() {
    this.setMutationObserver(false);
    this.setEventListeners(false);
  },
  methods: {
    setEventListeners(add = true) {
      const action = add ? 'addEventListener' : 'removeEventListener';
      window[action]('resize', this.debouncedOnWindowResize);
      window[action]('orientationchange', this.onWindowResize);
    },
    setMutationObserver(add = true) {
      if (!window || !window.MutationObserver) return;

      if (add && !this.observer) {
        this.observer = new MutationObserver(() => {
          this.calcItemsCount();
          this.setMutableValue(this.mutableValue);
          this.emitInput();
          this.adjustFrame();
        });
        this.observer.observe(this.$refs.frame, { childList: true });
        return;
      }

      if (!add && this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    valueToIndex(active) {
      const { itemsCount, clonesCount, actualPerPage } = this;

      if (!clonesCount) return active;

      let index = active + clonesCount;

      if (itemsCount - active <= Math.floor(actualPerPage / 2)) {
        index -= itemsCount;
      }

      return index;
    },
    indexToValue(index) {
      let value = index;

      const { itemsCount, clonesCount } = this;

      if (index < clonesCount) {
        value = index + (Math.ceil((clonesCount - index) / itemsCount) * itemsCount);
      } else if (index >= clonesCount + itemsCount) {
        value = index - (Math.ceil((index - (itemsCount + clonesCount) + 1) / itemsCount) * itemsCount);
      }

      return value - clonesCount;
    },
    getOffsetForIndex(index) {
      return (this.rtl ? 1 : -1) * index * (100 / this.actualPerPage);
    },
    calcItemsCount() {
      if (!this.$scopedSlots.default) {
        this.itemsCount = 0;
        return;
      }
      this.itemsCount = this.$scopedSlots.default().length;
    },
    shouldIgnoreDrag(e) {
      let i = 0;
      let ignore = false;

      while (i < this.ignoreDragging.length && !ignore) {
        ignore = e.target && e.target.matches(this.ignoreDragging[i]);
        i++;
      }

      return ignore;
    },
    // TODO: make more understandable methods slideToIndex and slideToCurrentIndex
    slideToIndex(index) {
      const targetIndex = this.itemsCount >= this.actualPerPage
        ? Math.min(Math.max(index, 0), this.itemsCount + (this.clonesCount * 2) - this.actualPerPage)
        : 0;

      if (this.currentIndex !== targetIndex) {
        this.$emit('slide-start');
      }

      this.currentIndex = targetIndex;
      this.mutableValue = this.indexToValue(this.currentIndex);
      this.slideToCurrentIndex(true);
      this.emitInput();
    },
    slideToCurrentIndex(withTransition) {
      requestAnimationFrame(() => {
        if (withTransition) {
          this.enableTransition();
        } else {
          this.disableTransition();
        }
        this.offset = `${this.getOffsetForIndex(this.currentIndex)}%`;
      });
    },
    adjustFrame() {
      const index = this.valueToIndex(this.mutableValue);
      if (index !== this.currentIndex) {
        this.currentIndex = index;
        this.slideToCurrentIndex(false);
      }
    },
    disableTransition() {
      this.isTransitionEnabled = false;
    },
    enableTransition() {
      this.isTransitionEnabled = true;
    },
    setMutableValue(value) {
      if (!this.clonesCount) {
        this.mutableValue = Math.max(Math.min(value, this.itemsCount - this.actualPerPage), 0);
      } else {
        this.mutableValue = Math.max(Math.min(value, this.itemsCount - 1), 0);
      }
    },
    emitInput() {
      this.$emit('input', this.mutableValue);
      this.$emit('change', this.mutableValue);
    },
    handleDragStart() {
      this.isPointerDown = true;
      this.disableTransition();
    },
    handleDrag() {
      if (!this.isDragging && this.dragStartX !== this.dragEndX) {
        this.isDragging = true;
        this.$emit('drag-start');
      }

      const percentOffset = this.getOffsetForIndex(this.currentIndex) / 100;
      const pxOffset = percentOffset * this.$refs.container.offsetWidth;
      const dragOffset = (this.dragEndX - this.dragStartX);
      const offset = this.rtl
        ? (pxOffset - dragOffset) * -1
        : pxOffset + dragOffset;
      this.offset = `${offset}px`;
    },
    handleDragEnd() {
      this.isPointerDown = false;
      if (this.dragEndX) {
        this.updateAfterDrag();
      }
      this.clearDrag();
      this.$emit('drag-end');
    },
    updateAfterDrag() {
      const movement = (this.rtl ? -1 : 1) * (this.dragEndX - this.dragStartX);
      const distance = Math.abs(movement);

      if (distance < this.threshold) {
        this.slideToCurrentIndex(true);
        return;
      }

      const slidesToSlide = Math.ceil(distance / (this.$refs.container.offsetWidth / this.actualPerPage));

      const index = this.currentIndex + (movement > 0 ? -slidesToSlide : slidesToSlide);

      this.slideToIndex(index);
    },
    clearDrag() {
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragEndX = 0;
      this.isHorizontalDrag = false;
      this.isDragging = false;
    },
    onTouchStart(e) {
      if (!this.active) return;
      if (this.shouldIgnoreDrag(e)) return;

      if (typeof this.handleTouchStartEvent === 'function') {
        this.handleTouchStartEvent(e);
      }

      this.dragStartX = e.touches[0].pageX;
      this.dragStartY = e.touches[0].pageY;
      this.handleDragStart();
    },
    onTouchMove(e) {
      if (!this.active) return;
      if (!this.isPointerDown) return;

      // TODO: do I need to check it?
      if (!this.isHorizontalDrag) {
        const movedY = Math.abs(this.dragStartY - e.touches[0].pageY);
        const movedX = Math.abs(this.dragStartX - e.touches[0].pageX);
        this.isHorizontalDrag = movedY < movedX;
      }

      if (!this.isHorizontalDrag) return;

      if (typeof this.handleTouchMoveEvent === 'function') {
        this.handleTouchMoveEvent(e);
      }

      this.dragEndX = e.touches[0].pageX;
      this.handleDrag();
    },
    onTouchEnd(e) {
      if (typeof this.handleTouchEndEvent === 'function') {
        this.handleTouchEndEvent(e);
      }
      this.handleDragEnd();
    },
    onMouseDown(e) {
      if (!this.active) return;
      if (this.shouldIgnoreDrag(e)) return;

      if (typeof this.handleMouseDownEvent === 'function') {
        this.handleMouseDownEvent(e);
      }

      this.dragStartX = e.pageX;
      this.handleDragStart();
    },
    onMouseMove(e) {
      if (!this.active) return;
      if (!this.isPointerDown) return;

      if (typeof this.handleMouseMoveEvent === 'function') {
        this.handleMouseMoveEvent(e);
      }

      this.dragPreventClick = true;
      this.dragEndX = e.pageX;
      this.handleDrag();
    },
    onMouseUp(e) {
      if (typeof this.handleMouseUpEvent === 'function') {
        this.handleMouseUpEvent(e);
      }

      this.handleDragEnd();
    },
    onMouseLeave(e) {
      if (!this.active) return;
      if (!this.isPointerDown) return;
      this.isPointerDown = false;
      this.dragEndX = e.pageX;
      this.updateAfterDrag();
      this.clearDrag();
      this.dragPreventClick = false;
    },
    onClick(e) {
      if (this.dragPreventClick) {
        e.preventDefault();
      }
      this.dragPreventClick = false;
    },
    onTransitionend() {
      this.adjustFrame();
      this.$emit('slide-end');
    },
    onWindowResize() {
      this.viewportWidth = getViewportWidth();
    },
    debouncedOnWindowResize: _debounce(function onWindowResize() {
      return this.onWindowResize();
    }, 300),
    // Public methods
    next() {
      this.slideToIndex(this.currentIndex + 1);
    },
    prev() {
      this.slideToIndex(this.currentIndex - 1);
    },
    slideTo(index) {
      // Change from first to last without jump in loop carousel
      if (this.clonesCount) {
        if (index === this.itemsCount - 1 && this.mutableValue === 0) {
          this.prev();
          return;
        }

        if (index === 0 && this.mutableValue === this.itemsCount - 1) {
          this.next();
          return;
        }
      }

      this.slideToIndex(this.currentIndex + (index - this.mutableValue));
    },
  },
  render(h) {
    let children = [];

    if (this.$scopedSlots.default) {
      const items = this.$scopedSlots.default();

      if (this.clonesCount) {
        const itemsBefore = items.slice(-this.clonesCount);
        if (itemsBefore.length < this.clonesCount) {
          itemsBefore.unshift(...items.slice(-this.clonesCount + itemsBefore.length));
        }

        const itemsAfter = items.slice(0, this.clonesCount);
        if (itemsAfter.length < this.clonesCount) {
          itemsAfter.push(...items.slice(0, this.clonesCount - itemsAfter.length));
        }

        children = [
          ...itemsBefore,
          ...items,
          ...itemsAfter,
        ];
      } else {
        children = [...items];
      }
    }

    const frame = h('div', {
      ref: 'frame',
      style: this.frameStyles,
      attrs: {
        tabindex: 1,
      },
      on: {
        transitionend: this.onTransitionend,
      },
    }, children.map((slot, index) => {
      const visible = index >= this.currentIndex && index < this.currentIndex + this.actualPerPage;
      const props = {
        style: this.itemStyles,
        attrs: {
          'aria-hidden': visible ? undefined : 'true',
          tabindex: -1,
        },
      };
      return h('div', props, [slot]);
    }));

    const self = this;

    return h('div', {
      ref: 'container',
      style: this.containerStyles,
      on: {
        touchstart: this.onTouchStart,
        touchmove: this.onTouchMove,
        touchend: this.onTouchEnd,
        mousedown(e) {
          if (e.button !== 0) return;
          self.onMouseDown(e);
        },
        mouseup: this.onMouseUp,
        mousemove: this.onMouseMove,
        mouseleave: this.onMouseLeave,
        click: this.onClick,
      },
    }, [frame]);
  },
};
